import { Component, inject, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NewsletterService } from '../../services/newsletter.service';
import { ToastService } from '../../services/toast.service';
import { ErrorMapperService } from '../../services/error-mapper.service';
import { LoadingModalComponent } from '../loading-modal/loading-modal.component';
import { ActionThrottleService } from '../../core/action-throttle.service';

@Component({
  selector: 'app-newsletter-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LoadingModalComponent],
  templateUrl: './newsletter-form.component.html',
  styleUrls: ['./newsletter-form.component.scss']
})
export class NewsletterFormComponent {
  private newsletterService = inject(NewsletterService);
  private toastService = inject(ToastService);
  private errorMapper = inject(ErrorMapperService);
  private fb = inject(FormBuilder);
  private throttle = inject(ActionThrottleService);

  @Output() subscribed = new EventEmitter<void>();

  newsletterForm: FormGroup;
  loading = false;

  constructor() {
    this.newsletterForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
  }

  get email() {
    return this.newsletterForm.get('email');
  }

  getErrorMessage(fieldName: string): string {
    const field = this.newsletterForm.get(fieldName);
    
    if (field?.hasError('required')) {
      return 'Email address is required';
    }
    if (field?.hasError('email')) {
      return 'Please enter a valid email address';
    }
    return '';
  }

  shouldShowError(fieldName: string): boolean {
    const field = this.newsletterForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  async onSubmit() {
    if (this.newsletterForm.invalid) {
      this.newsletterForm.markAllAsTouched();
      return;
    }

    // One request at a time, and a cooldown after every attempt (success or
    // failure). The server enforces the real limit (subscribeNewsletter).
    const result = await this.throttle.run('newsletter-subscribe', () => this.subscribe(), { cooldownMs: 10_000 })
      .catch(() => undefined);

    if (result && result.blocked && result.reason === 'cooldown') {
      this.toastService.warning(
        `Please wait ${this.throttle.waitSeconds(result)}s before trying again.`,
        'Slow down'
      );
    }
  }

  private async subscribe() {
    this.subscribed.emit();
    this.loading = true;
    this.newsletterForm.disable();

    try {
      await this.newsletterService.subscribe(this.email?.value);
      this.toastService.success('Successfully subscribed to our newsletter!', 'Welcome! 🎉');
      this.newsletterForm.reset();
    } catch (error: any) {
      console.error('Original error:', error);
      const mapped = this.errorMapper.map(error);
      this.toastService.error(mapped.userMessage || 'Failed to subscribe. Please try again.', 'Subscription Failed');
    } finally {
      this.loading = false;
      this.newsletterForm.enable();
    }
  }
}
